import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Input from '@mui/material/Input';
import { Translate, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { IUser } from 'app/shared/model/user.model';
import { getUsers } from 'app/modules/administration/user-management/user-management.reducer';
import { IDestination } from 'app/shared/model/destination.model';
import { getEntities as getDestinations } from 'app/entities/destination/destination.reducer';
import { ITour } from 'app/shared/model/tour.model';
import { getEntities as getTours } from 'app/entities/tour/tour.reducer';
import { ITourCategory } from 'app/shared/model/tour-category.model';
import { getEntities as getTourCategories } from 'app/entities/tour-category/tour-category.reducer';
import { IPlace } from 'app/shared/model/place.model';
import { getEntities as getPlaces } from 'app/entities/place/place.reducer';
import { IPlaceCategory } from 'app/shared/model/place-category.model';
import { getEntities as getPlaceCategories } from 'app/entities/place-category/place-category.reducer';
import { ITourExtraCategory } from 'app/shared/model/tour-extra-category.model';
import { getEntities as getTourExtraCategories } from 'app/entities/tour-extra-category/tour-extra-category.reducer';
import { ITourExtra } from 'app/shared/model/tour-extra.model';
import { getEntities as getTourExtras } from 'app/entities/tour-extra/tour-extra.reducer';
import { IVehicle } from 'app/shared/model/vehicle.model';
import { getEntities as getVehicles } from 'app/entities/vehicle/vehicle.reducer';
import { IDriver } from 'app/shared/model/driver.model';
import { getEntities as getDrivers } from 'app/entities/driver/driver.reducer';
import { IImageFile } from 'app/shared/model/image-file.model';
import { getEntity, updateEntity, createEntity, reset } from './image-file.reducer';

export const ImageFileUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const users = useAppSelector(state => state.userManagement.users);
  const account = useAppSelector(state => state.authentication.account);
  const destinations = useAppSelector(state => state.destination.entities);
  const tours = useAppSelector(state => state.tour.entities);
  const tourCategories = useAppSelector(state => state.tourCategory.entities);
  const places = useAppSelector(state => state.place.entities);
  const placeCategories = useAppSelector(state => state.placeCategory.entities);
  const tourExtraCategories = useAppSelector(state => state.tourExtraCategory.entities);
  const tourExtras = useAppSelector(state => state.tourExtra.entities);
  const vehicles = useAppSelector(state => state.vehicle.entities);
  const drivers = useAppSelector(state => state.driver.entities);
  const imageFileEntity = useAppSelector(state => state.imageFile.entity);
  const loading = useAppSelector(state => state.imageFile.loading);
  const updating = useAppSelector(state => state.imageFile.updating);
  const updateSuccess = useAppSelector(state => state.imageFile.updateSuccess);

  const [formData, setFormData] = useState<any>({
    createdBy: account?.id,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    navigate('/image-file' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
      setFormData({
        createdBy: account?.id,
      });
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getUsers({}));
    dispatch(getDestinations({}));
    dispatch(getTours({}));
    dispatch(getTourCategories({}));
    dispatch(getPlaces({}));
    dispatch(getPlaceCategories({}));
    dispatch(getTourExtraCategories({}));
    dispatch(getTourExtras({}));
    dispatch(getVehicles({}));
    dispatch(getDrivers({}));
  }, []);

  useEffect(() => {
    if (imageFileEntity) {
      setFormData({
        ...imageFileEntity,
        createdBy: imageFileEntity?.createdBy?.id,
        destination: imageFileEntity?.destination?.id,
        tour: imageFileEntity?.tour?.id,
        tourCategory: imageFileEntity?.tourCategory?.id,
        place: imageFileEntity?.place?.id,
        placeCategory: imageFileEntity?.placeCategory?.id,
        tourExtraCategory: imageFileEntity?.tourExtraCategory?.id,
        tourExtra: imageFileEntity?.tourExtra?.id,
        vehicle: imageFileEntity?.vehicle?.id,
        driver: imageFileEntity?.driver?.id,
      });
      if (imageFileEntity.data) {
        setImagePreview(`data:${imageFileEntity.dataContentType};base64,${imageFileEntity.data}`);
      }
    }
  }, [imageFileEntity]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.code || formData.code.trim() === '') {
      newErrors.code = 'Code is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        setFormData({
          ...formData,
          filename: file.name,
          data: base64,
          dataContentType: file.type,
        });
        setImagePreview(URL.createObjectURL(file));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearImage = () => {
    setFormData({
      ...formData,
      filename: '',
      data: '',
      dataContentType: '',
    });
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // eslint-disable-next-line complexity
  const saveEntity = () => {
    if (!validate()) {
      return;
    }

    const values = { ...formData };

    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }

    values.destination = values.destination || null;
    values.tour = values.tour || null;
    values.tourCategory = values.tourCategory || null;
    values.place = values.place || null;
    values.placeCategory = values.placeCategory || null;
    values.tourExtraCategory = values.tourExtraCategory || null;
    values.tourExtra = values.tourExtra || null;
    values.vehicle = values.vehicle || null;
    values.driver = values.driver || null;

    const entity = {
      ...imageFileEntity,
      ...values,
      createdBy: users.find(it => it.id.toString() === values.createdBy?.toString()),
      destination: destinations.find(it => it.id.toString() === values.destination?.toString()),
      tour: tours.find(it => it.id.toString() === values.tour?.toString()),
      tourCategory: tourCategories.find(it => it.id.toString() === values.tourCategory?.toString()),
      place: places.find(it => it.id.toString() === values.place?.toString()),
      placeCategory: placeCategories.find(it => it.id.toString() === values.placeCategory?.toString()),
      tourExtraCategory: tourExtraCategories.find(it => it.id.toString() === values.tourExtraCategory?.toString()),
      tourExtra: tourExtras.find(it => it.id.toString() === values.tourExtra?.toString()),
      vehicle: vehicles.find(it => it.id.toString() === values.vehicle?.toString()),
      driver: drivers.find(it => it.id.toString() === values.driver?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2">
          <Translate contentKey="jhipsterSampleApplicationApp.imageFile.home.createOrEditLabel">Create or edit a ImageFile</Translate>
        </Typography>
      </Box>
      <Box component="form" sx={{ '& .MuiTextField-root': { mb: 2 } }} noValidate autoComplete="off">
        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Box sx={{ '& .MuiTextField-root': { mb: 2 } }}>
            {!isNew ? (
              <TextField id="image-file-id" name="id" label={translate('global.field.id')} value={formData.id || ''} fullWidth InputProps={{ readOnly: true }} />
            ) : null}
            <TextField
              label={translate('jhipsterSampleApplicationApp.imageFile.code')}
              id="image-file-code"
              name="code"
              data-cy="code"
              value={formData.code || ''}
              onChange={handleChange('code')}
              fullWidth
              required
              error={!!errors.code}
              helperText={errors.code || ''}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.imageFile.title')}
              id="image-file-title"
              name="title"
              data-cy="title"
              value={formData.title || ''}
              onChange={handleChange('title')}
              fullWidth
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.imageFile.alt')}
              id="image-file-alt"
              name="alt"
              data-cy="alt"
              value={formData.alt || ''}
              onChange={handleChange('alt')}
              fullWidth
            />
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {translate('jhipsterSampleApplicationApp.imageFile.data') || 'Image File'}
              </Typography>
              <Input type="file" inputProps={{ accept: 'image/*' }} inputRef={fileInputRef} onChange={handleFileChange} fullWidth />
              {formData.filename && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Current file: {formData.filename}
                </Typography>
              )}
            </Box>
            {imagePreview && (
              <Box sx={{ mb: 2, position: 'relative' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Preview:
                </Typography>
                <Box
                  component="img"
                  src={imagePreview}
                  alt="Preview"
                  sx={{ maxWidth: '100%', maxHeight: 300, borderRadius: 1, border: '1px solid #ddd' }}
                />
                <Button size="small" color="error" onClick={handleClearImage} sx={{ mt: 1 }}>
                  Clear Image
                </Button>
              </Box>
            )}
            <FormControl fullWidth>
              <InputLabel id="image-file-destination-label">{translate('jhipsterSampleApplicationApp.imageFile.destination')}</InputLabel>
              <Select
                labelId="image-file-destination-label"
                id="image-file-destination"
                name="destination"
                data-cy="destination"
                value={formData.destination || ''}
                label={translate('jhipsterSampleApplicationApp.imageFile.destination')}
                onChange={handleSelectChange('destination')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {destinations
                  ? destinations.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="image-file-tour-label">{translate('jhipsterSampleApplicationApp.imageFile.tour')}</InputLabel>
              <Select
                labelId="image-file-tour-label"
                id="image-file-tour"
                name="tour"
                data-cy="tour"
                value={formData.tour || ''}
                label={translate('jhipsterSampleApplicationApp.imageFile.tour')}
                onChange={handleSelectChange('tour')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {tours
                  ? tours.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="image-file-tourCategory-label">{translate('jhipsterSampleApplicationApp.imageFile.tourCategory')}</InputLabel>
              <Select
                labelId="image-file-tourCategory-label"
                id="image-file-tourCategory"
                name="tourCategory"
                data-cy="tourCategory"
                value={formData.tourCategory || ''}
                label={translate('jhipsterSampleApplicationApp.imageFile.tourCategory')}
                onChange={handleSelectChange('tourCategory')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {tourCategories
                  ? tourCategories.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="image-file-place-label">{translate('jhipsterSampleApplicationApp.imageFile.place')}</InputLabel>
              <Select
                labelId="image-file-place-label"
                id="image-file-place"
                name="place"
                data-cy="place"
                value={formData.place || ''}
                label={translate('jhipsterSampleApplicationApp.imageFile.place')}
                onChange={handleSelectChange('place')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {places
                  ? places.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="image-file-placeCategory-label">
                {translate('jhipsterSampleApplicationApp.imageFile.placeCategory')}
              </InputLabel>
              <Select
                labelId="image-file-placeCategory-label"
                id="image-file-placeCategory"
                name="placeCategory"
                data-cy="placeCategory"
                value={formData.placeCategory || ''}
                label={translate('jhipsterSampleApplicationApp.imageFile.placeCategory')}
                onChange={handleSelectChange('placeCategory')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {placeCategories
                  ? placeCategories.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="image-file-tourExtraCategory-label">
                {translate('jhipsterSampleApplicationApp.imageFile.tourExtraCategory')}
              </InputLabel>
              <Select
                labelId="image-file-tourExtraCategory-label"
                id="image-file-tourExtraCategory"
                name="tourExtraCategory"
                data-cy="tourExtraCategory"
                value={formData.tourExtraCategory || ''}
                label={translate('jhipsterSampleApplicationApp.imageFile.tourExtraCategory')}
                onChange={handleSelectChange('tourExtraCategory')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {tourExtraCategories
                  ? tourExtraCategories.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="image-file-tourExtra-label">{translate('jhipsterSampleApplicationApp.imageFile.tourExtra')}</InputLabel>
              <Select
                labelId="image-file-tourExtra-label"
                id="image-file-tourExtra"
                name="tourExtra"
                data-cy="tourExtra"
                value={formData.tourExtra || ''}
                label={translate('jhipsterSampleApplicationApp.imageFile.tourExtra')}
                onChange={handleSelectChange('tourExtra')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {tourExtras
                  ? tourExtras.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="image-file-vehicle-label">{translate('jhipsterSampleApplicationApp.imageFile.vehicle')}</InputLabel>
              <Select
                labelId="image-file-vehicle-label"
                id="image-file-vehicle"
                name="vehicle"
                data-cy="vehicle"
                value={formData.vehicle || ''}
                label={translate('jhipsterSampleApplicationApp.imageFile.vehicle')}
                onChange={handleSelectChange('vehicle')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {vehicles
                  ? vehicles.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.plate}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="image-file-driver-label">{translate('jhipsterSampleApplicationApp.imageFile.driver')}</InputLabel>
              <Select
                labelId="image-file-driver-label"
                id="image-file-driver"
                name="driver"
                data-cy="driver"
                value={formData.driver || ''}
                label={translate('jhipsterSampleApplicationApp.imageFile.driver')}
                onChange={handleSelectChange('driver')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {drivers
                  ? drivers.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.name}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                component={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/image-file"
                replace
                color="info"
                variant="outlined"
                startIcon={<FontAwesomeIcon icon="arrow-left" />}
              >
                <Translate contentKey="entity.action.back">Back</Translate>
              </Button>
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                onClick={saveEntity}
                disabled={updating}
                variant="contained"
                startIcon={<FontAwesomeIcon icon="save" />}
              >
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ImageFileUpdate;
